// Writing a record's drafted facts to the server (#11880).
//
// This used to be the "Save Changes" button in the record header. The request
// is unchanged — every drafted fact, each with the baseline it was typed over,
// in one PATCH — but nobody clicks for it any more: the Facts grid calls `save`
// when the user closes the rows they were editing.

import { useCallback, useEffect, useRef, useState } from "react";
import { notifyManager, useQueryClient } from "@tanstack/react-query";
import {
  useEntityDraftStore,
  DELETED_FACT,
  FactBaseline,
} from "../Tabs/FactsTab";
import { updateEntity } from "../api";
import {
  EntityCreationFact,
  EntityFieldValue,
  EntityUpdatePayload,
  ErrorEntitiesViolateRule,
  ErrorSchemaInvalid,
  FactConflictResponse,
} from "../types";

export type SaveEntityFactsError =
  | ErrorEntitiesViolateRule
  | ErrorSchemaInvalid
  | string;

// Pull the structured conflict envelope out of the HttpError detail
// string. The backend encodes it as `fact_conflicts:<json>` because
// ninja's HttpError body is a plain string.
const parseConflictEnvelope = (err: unknown): FactConflictResponse | null => {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as { status?: number; message?: string; detail?: string };
  const raw = anyErr.detail ?? anyErr.message;
  if (typeof raw !== "string") return null;
  const prefix = "fact_conflicts:";
  const idx = raw.indexOf(prefix);
  if (idx === -1) return null;
  try {
    const parsed = JSON.parse(raw.slice(idx + prefix.length));
    if (parsed && Array.isArray(parsed.conflicts)) {
      return parsed as FactConflictResponse;
    }
  } catch {
    return null;
  }
  return null;
};

// Attach expected_value / expected_values to a drafted fact so the
// backend can OCC-check it (issue #4914). DELETED_FACT stays null; we
// don't yet ship an expected baseline for deletions — noted as a
// follow-up in the conflict-check handler.
const attachBaseline = (
  value: EntityCreationFact | null,
  baseline: FactBaseline | undefined
): EntityCreationFact | null => {
  if (value === null || baseline === undefined) return value;
  // The discriminated union's `value?: never` / `values?: never` mutual-
  // exclusion interferes with `"values" in value` narrowing, so pull
  // the concrete values out before rebuilding the right variant.
  const valuesArr = (value as { values?: EntityFieldValue[] }).values;
  const valueScalar = (value as { value?: EntityFieldValue }).value;
  if (baseline.isArray && valuesArr !== undefined) {
    return {
      values: valuesArr,
      expected_values: baseline.values ?? [],
    };
  }
  if (!baseline.isArray && valueScalar !== undefined) {
    return {
      value: valueScalar,
      expected_value: baseline.value ?? { scalar: null, entity: null },
    };
  }
  return value;
};

const NO_FACTS: ReadonlySet<string> = new Set();

export interface SaveEntityFacts {
  /**
   * Write the drafted facts of the rows named in `closed` — the rows the user
   * just left. A row still open is still being edited: the close that runs a
   * frame after its click can land after the user has opened the next row
   * and typed into it, and that half-typed value is not theirs to send yet
   * (#13359). A field marked as conflicted
   * is left out — the user has to act on the highlighted field first, as
   * the withheld Save button used to insist — and the rest still go, so one
   * rejected field does not silently hold every other edit hostage. A no-op
   * when nothing is drafted or the drafts belong to another record. A call
   * that lands while a save is in flight runs again once it settles, so a
   * row closed mid-request is not left holding a draft nobody asked to
   * write. Once the owning component has unmounted nothing re-runs: a later
   * run would find the store owned by the next tab, which for the same
   * record it cannot tell apart.
   */
  save: (closed: ReadonlySet<string>) => void;
  /**
   * The row `name` was opened again. If its close is waiting behind the
   * request in flight, take it off the queue: the user is editing it, and
   * its next close sends it (#13359). A row already on the wire is left
   * alone; its newer draft outlives that save.
   */
  withdraw: (name: string) => void;
  /**
   * Send whatever is drafted and not already on the wire, now, with no
   * queue — for the owner's unmount, where "again later" does not exist.
   * Independent of whether the unmount has already been noticed, so it does
   * not care which cleanup runs first.
   */
  flush: () => void;
  /** The facts the request in flight is carrying; empty between saves. */
  savingFacts: ReadonlySet<string>;
  /** The last save's failure, other than a conflict; cleared on the next attempt. */
  error: SaveEntityFactsError | null;
  clearError: () => void;
}

export const useSaveEntityFacts = (
  entityId: string,
  entityKind: string,
  entityName: string
): SaveEntityFacts => {
  const queryClient = useQueryClient();
  const [savingFacts, setSavingFacts] = useState<ReadonlySet<string>>(NO_FACTS);
  const [error, setError] = useState<SaveEntityFactsError | null>(null);
  const inFlight = useRef(false);
  const inFlightFacts = useRef<ReadonlySet<string>>(NO_FACTS);
  // The rows closed while a request was out, saved together once it settles.
  const queued = useRef<Set<string> | null>(null);
  const unmounted = useRef(false);
  useEffect(() => {
    // Reset on every mount, not only latched on unmount: a strict-mode or
    // Activity remount would otherwise be born "unmounted" and never queue.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  // The drafts a save would carry right now: everything drafted for this
  // record that is not conflicted and not already on the wire, limited to
  // `only` when it is given.
  const draftsToSend = useCallback(
    (exclude: ReadonlySet<string>, only: ReadonlySet<string> | null) => {
      const store = useEntityDraftStore.getState();
      const sent: Record<
        string,
        EntityCreationFact | null | typeof DELETED_FACT
      > = {};
      if (store.entityId !== entityId) return sent;
      for (const [key, value] of Object.entries(store.draftFacts)) {
        if (key in store.conflictFacts || exclude.has(key)) continue;
        if (only !== null && !only.has(key)) continue;
        sent[key] = value;
      }
      return sent;
    },
    [entityId]
  );

  const saveOnce = useCallback(
    async (exclude: ReadonlySet<string>, only: ReadonlySet<string> | null) => {
      const store = useEntityDraftStore.getState();
      // What is being written, as the store holds it, so the success path can
      // tell a draft that is now saved from one the user changed meanwhile.
      const sent = draftsToSend(exclude, only);
      if (Object.keys(sent).length === 0) {
        // Nothing to carry — including names a queued close announced as
        // "about to be saved" that have since been saved or withdrawn.
        setSavingFacts(NO_FACTS);
        return;
      }

      // Transform DELETED_FACT symbols to null for the API, and attach the OCC
      // baseline (expected_value / expected_values) captured when the user
      // first started editing each field.
      const transformedFacts: Record<string, EntityCreationFact | null> = {};
      for (const [key, value] of Object.entries(sent)) {
        const normalized =
          value === DELETED_FACT ? null : (value as EntityCreationFact);
        transformedFacts[key] = attachBaseline(
          normalized,
          store.baselineFacts[key]
        );
      }
      const payload: EntityUpdatePayload = {
        name: entityName, // Keep the same name
        facts: transformedFacts,
      };

      setError(null);
      const names: ReadonlySet<string> = new Set(Object.keys(sent));
      inFlightFacts.current = names;
      setSavingFacts(names);
      try {
        await updateEntity(queryClient, entityKind, entityId, payload);
        // The saved record is in the query cache, but the cache tells its
        // subscribers on the next tick. Dropping the draft before that lets a
        // row read the OLD value for a frame between the draft going and the
        // record arriving; waiting on the same scheduler puts this after the
        // notification it is waiting for.
        await new Promise<void>((resolve) => notifyManager.schedule(resolve));
        // The store may hold another record by now — the save that fires on
        // the click that navigates away lands after the next record's tab has
        // taken the store over. Its drafts are not ours to touch.
        if (useEntityDraftStore.getState().entityId !== entityId) return;
        useEntityDraftStore.getState().markFactsSaved(sent);
      } catch (err: unknown) {
        if (useEntityDraftStore.getState().entityId !== entityId) return;
        // 409 conflict: hand the conflict list to the draft store so each
        // affected field's editor paints red with the current DB value. The
        // store also rebaselines each conflict so the save after the user's
        // next edit overwrites (issue #4914).
        const envelope = parseConflictEnvelope(err);
        if (envelope) {
          useEntityDraftStore.getState().setConflicts(envelope.conflicts);
          (
            window as unknown as {
              openFpToast?: (message: string, kind: string) => void;
            }
          ).openFpToast?.(
            "This record was modified since you began editing. Please reload the page to see the latest changes.",
            "error"
          );
          return;
        }

        console.error("Failed to save entity:", err);
        if (err && typeof err === "object" && "message" in err) {
          // ErrorEntitiesViolateRule / ErrorSchemaInvalid, or a plain Error.
          setError(
            err instanceof Error
              ? err.message
              : (err as ErrorEntitiesViolateRule | ErrorSchemaInvalid)
          );
        } else if (typeof err === "string") {
          setError(err);
        } else {
          setError("Failed to save changes. Please try again.");
        }
      } finally {
        inFlightFacts.current = NO_FACTS;
        setSavingFacts(NO_FACTS);
      }
    },
    [entityId, entityKind, entityName, queryClient, draftsToSend]
  );

  const save = useCallback(
    (closed: ReadonlySet<string>) => {
      if (inFlight.current) {
        queued.current = new Set([...(queued.current ?? []), ...closed]);
        // A row closed behind an open request is as good as on the wire: it
        // reads its new value now rather than holding a blurred editor up
        // until the first request comes back.
        const waiting = Object.keys(
          draftsToSend(inFlightFacts.current, closed)
        );
        if (waiting.length > 0) {
          setSavingFacts((prev) => new Set([...prev, ...waiting]));
        }
        return;
      }
      inFlight.current = true;
      void saveOnce(NO_FACTS, closed).finally(() => {
        inFlight.current = false;
        const next = queued.current;
        queued.current = null;
        if (next !== null && !unmounted.current) save(next);
      });
    },
    [saveOnce, draftsToSend]
  );

  const withdraw = useCallback((name: string) => {
    if (queued.current === null || !queued.current.has(name)) return;
    const rest = new Set(queued.current);
    rest.delete(name);
    queued.current = rest;
    if (inFlightFacts.current.has(name)) return;
    setSavingFacts((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  }, []);

  // Every draft, open row or not: the tab is going, and nothing is left to
  // close the rest.
  const flush = useCallback(() => {
    void saveOnce(inFlightFacts.current, null);
  }, [saveOnce]);

  const clearError = useCallback(() => setError(null), []);

  return { save, withdraw, flush, savingFacts, error, clearError };
};
